import { z } from "zod";
import {
  toRichText,
  type TLComment,
  type TLCommentThread,
  type TLRecord,
  type TLShape,
} from "@tldraw/tlschema";
import type { ChangeTrackingStorage } from "./change-tracking-storage";
import type { Transaction } from "./sync/storage";
import { schema } from "./schema";
import { AGENT_MENTION_LABEL, agentMentionsIn, mentionText } from "./mentions";
import {
  agentSlot,
  draftScope,
  recordLabel,
  refreshScope,
  type AgentDraft,
} from "./agent-drafts";
import type { AgentSnapshot } from "./agent-board";

/*
 * @Agent in a comment. A comment is already pinned to a shape, a point or a
 * region, so it is a located request: the pin decides what the agent works on
 * and the harness, not the model, posts the reply back into the thread.
 */

/** Body of the call a client makes after posting a comment with @Agent. */
export const commentCall = z
  .object({
    threadId: z.string().startsWith("comment-thread:").max(160),
    commentId: z.string().startsWith("comment:").max(160),
  })
  .strict();
/** Body of Undo/Redo from an agent reply. */
export const threadUndo = z
  .object({ batchId: z.string().uuid(), redo: z.boolean() })
  .strict();

/** Author ID of an agent reply. Never a project member, so it never receives notifications. */
export const agentAuthor = (conversationId: string) => `agent:${conversationId}`;
export const isAgentAuthor = (authorId: string) => authorId.startsWith("agent:");

export type AgentReplyMeta = {
  conversationId: string;
  callerId: string;
  /** The board action this turn applied, or null when it only replied. */
  batchId: string | null;
  /** Objects the turn added or changed, for jump buttons. */
  changed: string[];
  undone: boolean;
};
export function agentReplyMeta(comment: TLComment): AgentReplyMeta | null {
  const meta = comment.meta.agentReply as AgentReplyMeta | undefined;
  return isAgentAuthor(comment.authorId) &&
    meta &&
    typeof meta.conversationId === "string"
    ? meta
    : null;
}

type Box = { x: number; y: number; w: number; h: number };
const shapeSize = (s: TLShape) => {
  const p = s.props as { w?: unknown; h?: unknown };
  return {
    w: typeof p.w === "number" ? p.w : 0,
    h: typeof p.h === "number" ? p.h : 0,
  };
};
/** Page bounds from stored geometry: parent-local positions, rotations and w/h props. */
export function shapePageBox(
  store: Record<string, TLRecord>,
  shape: TLShape,
): Box {
  const { w, h } = shapeSize(shape);
  let points = [
    { x: 0, y: 0 },
    { x: w, y: 0 },
    { x: w, y: h },
    { x: 0, y: h },
  ];
  let current: TLRecord | undefined = shape;
  for (let depth = 0; current?.typeName === "shape" && depth < 32; depth++) {
    const s: TLShape = current;
    const cos = Math.cos(s.rotation),
      sin = Math.sin(s.rotation);
    points = points.map((p) => ({
      x: s.x + p.x * cos - p.y * sin,
      y: s.y + p.x * sin + p.y * cos,
    }));
    current = store[s.parentId];
  }
  const xs = points.map((p) => p.x),
    ys = points.map((p) => p.y);
  const x = Math.min(...xs),
    y = Math.min(...ys);
  return { x, y, w: Math.max(...xs) - x, h: Math.max(...ys) - y };
}
function pageOf(store: Record<string, TLRecord>, shape: TLShape) {
  let id: string = shape.parentId;
  for (let depth = 0; depth < 32 && store[id]?.typeName === "shape"; depth++)
    id = (store[id] as TLShape).parentId;
  return id;
}
const overlaps = (a: Box, b: Box) =>
  a.x <= b.x + b.w && b.x <= a.x + a.w && a.y <= b.y + b.h && b.y <= a.y + a.h;
// Half a unit of slack, so a region drawn exactly around a shape still holds it.
const within = (a: Box, b: Box) =>
  a.x >= b.x - 0.5 &&
  a.y >= b.y - 0.5 &&
  a.x + a.w <= b.x + b.w + 0.5 &&
  a.y + a.h <= b.y + b.h + 0.5;
const contains = (a: Box, p: { x: number; y: number }) =>
  p.x >= a.x && p.x <= a.x + a.w && p.y >= a.y && p.y <= a.y + a.h;

export type AnchorFocus = { ids: string[]; label: string };
/**
 * What a pinned comment asks the agent to work on: the pinned shape, the
 * shapes inside a region (a shape wholly inside counts as itself, a container
 * it only crosses as context), or the sheet under a point. The caller's
 * current selection plays no part.
 */
export function anchorFocus(
  store: Record<string, TLRecord>,
  thread: TLCommentThread,
): AnchorFocus {
  const anchor = thread.anchor;
  if (anchor.type === "shape") {
    const shape = store[anchor.shapeId];
    if (shape?.typeName !== "shape")
      throw new Error("The object this comment is pinned to was removed.");
    return { ids: [shape.id], label: recordLabel(shape) };
  }
  const shapes = Object.values(store).filter(
    (r): r is TLShape =>
      r.typeName === "shape" && pageOf(store, r) === thread.pageId,
  );
  const boxes = new Map(shapes.map((s) => [s.id, shapePageBox(store, s)]));
  const innermost = (found: TLShape[]) => {
    const ids = new Set<string>(found.map((s) => s.id));
    // A container that holds another match is context, not the focus.
    for (const s of found) {
      let parent = store[s.parentId];
      for (let depth = 0; parent?.typeName === "shape" && depth < 32; depth++) {
        ids.delete(parent.id);
        parent = store[parent.parentId];
      }
    }
    return found.filter((s) => ids.has(s.id));
  };
  if (anchor.type === "region") {
    const region = { x: anchor.x, y: anchor.y, w: anchor.w, h: anchor.h };
    const found = shapes.filter((s) => overlaps(boxes.get(s.id)!, region));
    // A container wholly inside the area (a Cmd+K region around two sheets)
    // is the focus itself, and its contents come with it.
    const whole = new Set(
      found.filter((s) => within(boxes.get(s.id)!, region)).map((s) => s.id),
    );
    const inside = innermost(
      found.filter((s) => {
        let parent = store[s.parentId];
        for (let depth = 0; parent?.typeName === "shape" && depth < 32; depth++) {
          if (whole.has(parent.id)) return false;
          parent = store[parent.parentId];
        }
        return true;
      }),
    );
    if (inside.length > 100)
      throw new Error(
        "This area holds more than 100 objects. Mark a smaller area.",
      );
    if (inside.length)
      return {
        ids: inside.map((s) => s.id),
        label:
          inside.length === 1
            ? recordLabel(inside[0])
            : `${inside.length} objects in the marked area`,
      };
    return { ids: [], label: "The marked area" };
  }
  if (anchor.type === "point") {
    const sheet = shapes
      .filter((s) => s.type === "sheet" && contains(boxes.get(s.id)!, anchor))
      .sort((a, b) => (a.index < b.index ? 1 : a.index > b.index ? -1 : 0))[0];
    if (sheet) return { ids: [sheet.id], label: recordLabel(sheet) };
    return { ids: [], label: "This spot on the canvas" };
  }
  return { ids: [], label: "This page" };
}

export type CommentTrigger = {
  thread: TLCommentThread;
  comment: TLComment;
  /** Duplicate-run keys: new @Agent occurrences, or the reply that answers a question. */
  keys: string[];
  message: string;
};
/**
 * Check that a posted comment may start or continue the caller's thread
 * conversation, and that this occurrence has not run before.
 */
export function commentTrigger(
  store: Record<string, TLRecord>,
  input: { threadId: string; commentId: string },
  callerId: string,
  draft?: AgentDraft,
): CommentTrigger {
  const thread = store[input.threadId],
    comment = store[input.commentId];
  if (thread?.typeName !== "comment-thread" || thread.isDeleted)
    throw new Error("This discussion is unavailable.");
  if (
    comment?.typeName !== "comment" ||
    comment.threadId !== thread.id ||
    comment.isDeleted
  )
    throw new Error("This comment is unavailable.");
  if (comment.authorId !== callerId)
    throw new Error("Only the comment's author can call the agent with it.");
  const handled = new Set(draft?.handled ?? []);
  const keys = agentMentionsIn(comment.body).filter((id) => !handled.has(id));
  // An answer to the agent's question continues the run without another
  // @Agent. It must be new since the agent last read the board; comparing
  // client and server clocks would be unreliable.
  if (
    !keys.length &&
    draft?.status === "waiting" &&
    !handled.has(comment.id) &&
    !draft.head[comment.id]
  )
    keys.push(comment.id);
  if (!keys.length)
    throw new Error("This comment has no new @Agent request.");
  const message = mentionText(comment.body).trim();
  if (!message || message.length > 4000)
    throw new Error("Write a request of up to 4,000 characters.");
  return { thread, comment, keys, message };
}

/** A new conversation called from a comment: private to the caller, focused by the pin. */
export function threadDraft(input: {
  id: string;
  roomId: string;
  userId: string;
  agent: number;
  state: AgentSnapshot;
  thread: TLCommentThread;
}): AgentDraft {
  const focus = anchorFocus(input.state.store, input.thread);
  const now = Date.now();
  return {
    id: input.id,
    roomId: input.roomId,
    userId: input.userId,
    title: "New conversation",
    status: "new",
    created: now,
    updated: now,
    version: 0,
    baseRevision: input.state.revision,
    scopeIds: focus.ids.length ? draftScope(input.state, focus.ids) : [],
    contextLabel: focus.label.slice(0, 160),
    agent: input.agent,
    threadId: input.thread.id,
    handled: [],
    base: input.state.store,
    head: structuredClone(input.state.store),
    steps: [],
    sources: [],
    messages: [],
    error: "",
    runId: null,
    appliedBatchId: null,
  };
}

/**
 * Editable scope for the next turn: the conversation's scope refreshed as for
 * any conversation (focus, objects the user allowed, what it made), plus the
 * pin's current focus for a thread.
 */
export function turnScope(draft: AgentDraft, live: AgentSnapshot): string[] {
  const thread = draft.threadId ? live.store[draft.threadId] : undefined;
  if (draft.threadId && thread?.typeName !== "comment-thread")
    throw new Error("This discussion is unavailable.");
  const focus =
    thread?.typeName === "comment-thread"
      ? anchorFocus(live.store, thread).ids
      : [];
  return refreshScope(
    { ...draft, scopeIds: [...draft.scopeIds, ...focus] },
    live.store,
  );
}

/** Record the comment that called this turn, so the same occurrence never runs twice. */
export function markHandled(draft: AgentDraft, keys: string[]) {
  draft.handled = [...(draft.handled ?? []), ...keys].slice(-200);
}

/** The reply text and changed objects for one finished turn. */
export function turnReply(
  d: AgentDraft,
  from: { step: number; message: number },
  outcome?: string,
) {
  const text =
    d.messages
      .slice(from.message)
      .filter((m) => m.role === "agent" && m.text.trim())
      .map((m) => m.text.trim())
      .join("\n\n") ||
    outcome ||
    "Done.";
  const changed = new Set<string>();
  for (const step of d.steps.slice(from.step))
    if (!step.kind)
      for (const [id, change] of Object.entries(step.changes))
        if (change.after?.typeName === "shape" && d.head[id]) changed.add(id);
  return { text, changed: [...changed] };
}

/** The application a thread Undo or Redo button refers to, only while it is next in the history. */
export function threadReverseTarget(
  d: AgentDraft,
  batchId: string,
  redo: boolean,
) {
  if (!d.threadId) throw new Error("This conversation has no discussion.");
  if (d.status === "working")
    throw new Error("Wait for the agent to finish before undo or redo.");
  const cursor = d.historyCursor ?? d.applications?.length ?? 0;
  const action = d.applications?.[redo ? cursor : cursor - 1];
  if (action?.batchId !== batchId)
    throw new Error(
      redo
        ? "Redo the agent's earlier change first."
        : "Undo the agent's later change first.",
    );
  return action;
}

/** Earlier comments in the thread, as untrusted context for the prompt. */
export function threadContext(
  store: Record<string, TLRecord>,
  threadId: string,
  before: number,
) {
  return Object.values(store)
    .filter(
      (r): r is TLComment =>
        r.typeName === "comment" &&
        r.threadId === threadId &&
        !r.isDeleted &&
        r.createdAt < before,
    )
    .sort((a, b) => a.createdAt - b.createdAt)
    .slice(-20)
    .map((c) => ({
      author: isAgentAuthor(c.authorId)
        ? "agent"
        : String(c.meta.author || "Member").slice(0, 80),
      text: mentionText(c.body).slice(0, 600),
    }));
}

/** The agent's reply comment. Its ID is derived from the run so a retried write is safe. */
export function agentReply(
  d: AgentDraft,
  thread: TLCommentThread,
  input: {
    runId: string;
    text: string;
    batchId: string | null;
    changed: string[];
  },
): TLComment {
  const at = Date.now();
  return {
    id: `comment:agent_${input.runId}` as TLComment["id"],
    typeName: "comment",
    threadId: thread.id,
    pageId: thread.pageId,
    authorId: agentAuthor(d.id),
    createdAt: at,
    editedAt: null,
    body: toRichText(input.text.slice(0, 3000)),
    isDeleted: false,
    meta: {
      author: AGENT_MENTION_LABEL,
      agentNumber: agentSlot(d),
      agentReply: {
        conversationId: d.id,
        callerId: d.userId,
        batchId: input.batchId,
        changed: input.changed.slice(0, 20),
        undone: false,
      } satisfies AgentReplyMeta,
    },
  };
}

/**
 * Write or update an agent reply in its own transaction. It is never part of
 * the journaled action, so Undo of the agent's change keeps the reply.
 */
export function writeAgentReply(
  storage: ChangeTrackingStorage,
  validate: (txn: Transaction<TLRecord>) => void,
  reply: TLComment,
  name: string,
) {
  storage.actor = { id: reply.authorId, name, session: reply.authorId };
  try {
    storage.transaction((txn) => {
      const thread = txn.get(reply.threadId);
      if (thread?.typeName !== "comment-thread" || thread.isDeleted) return;
      const existing = txn.get(reply.id);
      const next =
        existing?.typeName === "comment"
          ? { ...existing, meta: { ...existing.meta, ...reply.meta } }
          : reply;
      schema.types.comment.validate(next);
      txn.set(next.id, next);
      validate(txn);
    });
  } finally {
    storage.actor = null;
  }
}

/** Mark whether a thread reply's change is currently undone, so every viewer's Undo/Redo is right. */
export function markAgentReply(
  storage: ChangeTrackingStorage,
  validate: (txn: Transaction<TLRecord>) => void,
  replyId: string,
  undone: boolean,
) {
  const reply = storage.transaction((txn) => txn.get(replyId)).result;
  if (reply?.typeName !== "comment" || !agentReplyMeta(reply)) return;
  writeAgentReply(
    storage,
    validate,
    {
      ...reply,
      meta: {
        ...reply.meta,
        agentReply: { ...agentReplyMeta(reply)!, undone },
      },
    },
    AGENT_MENTION_LABEL,
  );
}
